import { theme } from 'antd';
import { CredentialIcon } from 'assets/images/svg';
import { useManagePermittedRoles } from 'library/hooks';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { credentialsActions } from 'reduxStore/credentials/slice';
import { selectCredentialByID, selectCredentialLoading } from 'reduxStore/credentials/selectors';
import { selectPeople } from 'reduxStore/people/selectors';
import { Button, Flex, Form, Modal, Select, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const AssignPersonToCredentialModal = ({
	visible: visibleProp,
	setVisible: setVisibleProp,
	credentialID,
	children
}) => {
	const canManageCredential = useManagePermittedRoles('updateCredential');
	const permissionDeniedMessage = "You don't have permission to perform this action.";
	const {
		token: {
			paddingXL,
			fontSizeSM,
			marginLG,
			colorTextDescription,
			fontWeightStrong,
			colorBorderSecondary,
			paddingSM
		}
	} = theme.useToken();

	const dispatch = useDispatch();
	const [form] = Form.useForm();

	const [internalVisible, setInternalVisible] = useState(false);
	const visible = visibleProp !== undefined ? visibleProp : internalVisible;
	const setVisible = setVisibleProp !== undefined ? setVisibleProp : setInternalVisible;

	const people = useSelector(selectPeople);
	const credential = useSelector((state) => selectCredentialByID(state, credentialID));
	const isCredentialLoading = !!useSelector((state) => selectCredentialLoading(state, credentialID));

	const personOptions = people.map((p) => ({
		value: p.personID,
		label: p.displayName
	}));
	const isAssigned = Boolean(credential?.personID);
	const isTriggerDisabled = !canManageCredential || isCredentialLoading || isAssigned;
	const triggerTitle = !canManageCredential
		? permissionDeniedMessage
		: isAssigned
			? 'Credential is already assigned. Unassign it first.'
			: 'Assign Credential';

	const handleCancel = () => {
		form.resetFields();
		setVisible(false);
	};

	const handleFinish = ({ personID }) => {
		dispatch(
			credentialsActions.assignCredential({
				credentialID,
				data: { newPersonID: personID }
			})
		);

		handleCancel();
	};

	return (
		<>
			<Tooltip title={triggerTitle}>
				<span>
					{children ? (
						children
					) : (
						<Button
							type='text'
							size='small'
							icon={<CredentialIcon color='currentColor' />}
							onClick={(e) => {
								e.stopPropagation();
								!isTriggerDisabled && setVisible(true);
							}}
							disabled={isTriggerDisabled}
						/>
					)}
				</span>
			</Tooltip>
			<Modal
				open={visible}
				onCancel={handleCancel}
				width={425}
				centered
				closeIcon={false}
				destroyOnClose
				styles={{
					content: {
						padding: 0
					},
					body: { padding: paddingXL },
					footer: {
						padding: paddingXL,
						borderTop: `1px solid ${colorBorderSecondary}`
					}
				}}
				footer={[
					<Flex gap={paddingSM} align='center' justify='flex-end'>
						<Button key='cancel' onClick={handleCancel} disabled={isCredentialLoading}>
							Cancel
						</Button>
						<Button
							type='primary'
							htmlType='submit'
							key='submit'
							form={form}
							loading={isCredentialLoading}
						>
							Assign Credential
						</Button>
					</Flex>
				]}
			>
				<Title level={4} style={{ margin: 0 }}>
					Assign Credential to Person
				</Title>
				<Text style={{ lineHeight: '20px', fontSize: fontSizeSM, color: colorTextDescription }}>
					Assigning a Credential to a Person links it to their access permissions, allowing the Credential to
					be used for authentication and entry wherever the Person has access.
				</Text>

				<Form id={form} form={form} layout='vertical' onFinish={handleFinish} style={{ marginTop: marginLG }}>
					<Form.Item
						name='personID'
						label='Person'
						rules={[{ required: true, message: 'Please select a person' }]}
						required={false}
						style={{ fontWeight: fontWeightStrong, marginBottom: 42 }}
					>
						<Select
							options={personOptions}
							placeholder='John Doe'
							showSearch
							filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
							style={{ fontWeight: 400 }}
						/>
					</Form.Item>
				</Form>
			</Modal>
		</>
	);
};

export default AssignPersonToCredentialModal;
